import { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import { IClinicMember, InviteCustomerRequest, IPatient, MembershipStatus } from "../library/models/clinic";
import { IPagedQueryOptions } from "../library/models/page";
import { ISession } from "../library/models/session";
import { IUserPicture } from "../library/models/userPicture";
import { InvitedUser, InviteTherapistRequest } from "../library/models/clinicAdmin";
import { SupportedLanguage } from "../library/models/language";
import { User, UserRole } from "../types";
import { BaseService } from "./baseService";

/**
 * How the patients listing may be narrowed. Every one of these is a query parameter the
 * server applies, never a filter over what came back.
 */
export interface IPatientFilters {
    /** Only patients the signed-in therapist is one of the therapists of. */
    mine?: boolean;
    /** Matched against the patient's own name, email address and phone number. */
    search?: string;
    /** One membership status, or undefined for every one. */
    status?: MembershipStatus;
}

/**
 * The people in the caller's own clinic, served by the tenant-scoped /users endpoints.
 * Which clinic that is comes from the token, never from an argument -- the mirror image
 * of ClinicsService, where the platform owner names the clinic on every call.
 */
export class UsersService extends BaseService {
    /**
     * Everyone at the clinic holding the given role, whatever state their membership is
     * in -- accepted, invited, or removed. Wider than GET /users on purpose: without the
     * pending rows an invitation produces no visible change, and without the revoked ones
     * a removed therapist simply disappears with no way to bring them back.
     */
    getClinicMembers(role: UserRole): UseQueryOptions<IClinicMember[], Error> {
        return this.createGetQuery<IClinicMember[]>('/users/members', { role });
    }

    /**
     * One page of the clinic's patients, for a therapist. Its own endpoint rather than
     * getClinicMembers('CUSTOMER') because that one is admin-only: there the role is a
     * query parameter, so opening it to therapists would also let them list each other.
     * Here the server fixes the role and the clinic comes from the token.
     *
     * Every filter is a request. It used to send `mine` and nothing else, and the page
     * narrowed the array it got back by name and by status -- which could only ever find
     * what happened to have been downloaded, quietly required the whole list to be there,
     * and made "3 of 40" a count of the browser's memory. The search also matched with
     * `String.includes`, which is accent-sensitive, so "Farcas" never found "Fărcaș".
     * All three now happen in SQL, where the collation is accent-insensitive and where the
     * rules already live.
     *
     * Each filter is folded into the query's `data`, which makes it part of the query key
     * for free: React Query caches per term, so backspacing to a term already searched is
     * instant. Omitted when off, so "no filters" keeps one stable key. Debounce the term
     * before it gets here -- see `useDebouncedValue`.
     *
     * Every row names the therapists the patient belongs to -- those who evaluated them
     * and those who provide their services -- so `mine` is the same union read for one id
     * rather than a different question.
     */
    getPatients(size: number, filters: IPatientFilters = {}): IPagedQueryOptions<IPatient> {
        const { mine, search, status } = filters;

        return this.createGetInfiniteQuery<IPatient>('/users/patients', size, {
            ...(mine ? { mine: true } : {}),
            ...(search?.trim() ? { search: search.trim() } : {}),
            ...(status ? { status } : {}),
        });
    }

    /**
     * One patient, for their own page.
     *
     * Its own request rather than a lookup in the listing, which pages: the patient being
     * opened is not necessarily on the page the app happens to hold, and finding them in a
     * fetched array was the same fetch-everything-and-filter the search box was doing.
     */
    getPatient(patientId: number): UseQueryOptions<IPatient, Error> {
        return this.createGetQuery<IPatient>(`/users/patients/${patientId}`);
    }

    inviteTherapistMutation(): UseMutationOptions<InvitedUser, Error, InviteTherapistRequest> {
        return this.createMutation<InvitedUser, InviteTherapistRequest>('/users');
    }

    /**
     * Invites somebody as a patient of the caller's clinic -- and, called a second time
     * for a patient who never accepted, is how their invitation is re-sent. There is no
     * resend endpoint to call instead: the server reuses the account, the membership and
     * the code it already made, and sends the same email with the same link. A patient's
     * link has no expiry.
     *
     * Its own method rather than a widening of inviteTherapistMutation: that one posts to
     * /users, which is admin-only and fixes the role to THERAPIST.
     */
    inviteCustomerMutation(): UseMutationOptions<InvitedUser, Error, InviteCustomerRequest> {
        return this.createMutation<InvitedUser, InviteCustomerRequest>('/users/customers');
    }

    /**
     * Takes back an invitation a patient has not accepted, so its link stops working.
     *
     * This is the only thing that ends one: the link does not expire, so an invitation
     * sent to a mistyped address stays good until somebody withdraws it. The API refuses
     * once the patient has accepted -- removing somebody with access is the admin's
     * `DELETE /users/{id}`, not this.
     *
     * Written out rather than through `createMutation(url, 'delete')`, which would send
     * the id as `?id=` and not in the path.
     */
    withdrawInvitationMutation(): UseMutationOptions<void, Error, number> {
        return {
            mutationFn: (patientId: number): Promise<void> =>
                this.delete<void>(`/users/customers/${patientId}/invitation`),
        };
    }

    /**
     * The signed-in caller and the role their session holds, read fresh. Not clinic-scoped,
     * so it answers the platform owner too. `AuthContext` is its only caller.
     */
    getMe(): UseQueryOptions<ISession, Error> {
        return this.createGetQuery<ISession>('/users/me');
    }

    /**
     * Stores which language the caller reads. Fired by the picker in the header, not by a
     * form -- so it is its own route with no id in it rather than a field on the profile
     * update, and it cannot carry a name and a phone number it never showed.
     */
    updateLanguageMutation(): UseMutationOptions<User, Error, { language: SupportedLanguage }> {
        return {
            mutationFn: (body: { language: SupportedLanguage }): Promise<User> =>
                this.put<User>('/users/me/language', body),
        };
    }

    /**
     * Removes somebody from the clinic. A soft delete on the server: the membership is
     * revoked and the account survives, along with their access to any other clinic.
     *
     * Hand-written because createMutation's delete branch appends `?id=`, while this API
     * takes the id in the path.
     */
    revokeMemberMutation(): UseMutationOptions<void, Error, number> {
        return {
            mutationFn: (id: number): Promise<void> => this.delete<void>(`/users/${id}`),
        };
    }

    /**
     * Sets the signed-in user's own profile picture.
     *
     * The two picture routes are the only things here that name no clinic and take no id.
     * `/users/me/picture` means exactly that: the server resolves the subject from the token,
     * so there is no id to pass and none that would be accepted -- nobody sets anybody else's
     * picture, the same rule that already governs editing a name.
     *
     * `Content-Type: undefined` is not a mistake, and is the same detail `uploadLogoMutation`
     * carries: this axios instance defaults every request to `application/json`, and a
     * multipart body needs a boundary parameter only the browser can generate. Clearing the
     * header is what lets it. Setting `multipart/form-data` by hand produces a boundary-less
     * header and a body the server cannot parse.
     *
     * Resolves with the new URL rather than nothing, because the header avatar is on screen
     * at the moment this returns and `AuthContext` rehydrates from localStorage rather than
     * from the server -- so the caller needs the URL in hand to keep both honest.
     */
    uploadPictureMutation(): UseMutationOptions<IUserPicture, Error, File> {
        return {
            mutationFn: (file: File): Promise<IUserPicture> => {
                const body = new FormData();
                body.append('file', file);

                return this.put<IUserPicture>('/users/me/picture', body, {
                    headers: { 'Content-Type': undefined },
                });
            },
        };
    }

    /** Removes it; the person falls back to their initial everywhere they are drawn. */
    removePictureMutation(): UseMutationOptions<void, Error, void> {
        return {
            mutationFn: (): Promise<void> => this.delete<void>('/users/me/picture'),
        };
    }

    /**
     * Fetches a stored picture back as a file, so the crop dialog can reopen it.
     *
     * Deliberately not a React Query option object like the reads above: a one-shot fetch
     * fired by a button, not state a page renders, and caching it would hand back the
     * previous picture to somebody re-cropping straight after a save. Same shape, and the
     * same reasoning, as `fetchLogoFile`.
     *
     * No `?v=` cache-buster, which is the one way it differs: a logo's path is stable for the
     * life of the clinic, and a picture's changes on every upload because the token does.
     */
    async fetchPictureFile(pictureUrl: string): Promise<File> {
        const blob: Blob = await this.get<Blob>(pictureUrl, { responseType: 'blob' });

        return new File([blob], 'picture.jpg', { type: blob.type || 'image/jpeg' });
    }
}
